/**
 * TrialBanner — sticky top-of-page alert shown when the workspace's trial
 * is about to expire, has expired, or is in a write-locked state.
 *
 * Visibility rules:
 *   - Hidden when subscription is null or status is `active`/`canceled`.
 *   - Warning (amber) when trialing with ≤ 3 days left.
 *   - Critical (red) when trialing with 0 days left or writeEnabled is false.
 *
 * The banner is display-only — enforcement is server-side via the write gate.
 */
import { AlertTriangle, Lock, MessageCircle } from "lucide-react";
import { cn } from "@flint/ui";

const TALK_URL = "https://wa.me/917012345678?text=Hi%2C+I%27d+like+to+activate+my+Flint+plan";

export interface TrialBannerProps {
  plan: string;
  status: string;
  trialDaysLeft: number | null;
  writeEnabled: boolean;
}

export function TrialBanner({ status, trialDaysLeft, writeEnabled }: TrialBannerProps) {
  // Determine what (if anything) to show
  const isLocked = !writeEnabled;
  const isExpired = status === "trial_expired";
  const isTrialCritical = status === "trialing" && trialDaysLeft !== null && trialDaysLeft <= 0;
  const isTrialWarning = status === "trialing" && trialDaysLeft !== null && trialDaysLeft > 0 && trialDaysLeft <= 3;

  if (!isLocked && !isExpired && !isTrialCritical && !isTrialWarning) return null;

  const isCritical = isLocked || isExpired || isTrialCritical;

  return (
    <div
      role="alert"
      className={cn(
        "flex items-center gap-3 px-4 py-2.5 text-sm font-medium border-b",
        isCritical
          ? "bg-bad-50 text-bad border-bad-100"
          : "bg-warn-50 text-warn border-warn-100",
      )}
    >
      {isCritical ? (
        <Lock className="size-4 shrink-0" aria-hidden />
      ) : (
        <AlertTriangle className="size-4 shrink-0" aria-hidden />
      )}

      <span className="flex-1 min-w-0">
        {isLocked && "Your workspace is in read-only mode — no new entries can be saved."}
        {!isLocked && isExpired && "Your free trial has ended. Your data is safe — activate to continue working."}
        {!isLocked && !isExpired && isTrialCritical && "Your trial expires today. Activate now to avoid interruption."}
        {!isLocked && !isExpired && !isTrialCritical && isTrialWarning &&
          `Your free trial ends in ${trialDaysLeft} day${trialDaysLeft === 1 ? "" : "s"}.`}
      </span>

      <a
        href={TALK_URL}
        target="_blank"
        rel="noreferrer noopener"
        className={cn(
          "shrink-0 inline-flex items-center gap-1.5 px-3 py-1 rounded-sm text-xs font-semibold transition-colors",
          isCritical
            ? "bg-bad text-white hover:bg-bad/90"
            : "bg-warn text-white hover:bg-warn/90",
        )}
      >
        <MessageCircle className="size-3.5" aria-hidden />
        Talk to us
      </a>
    </div>
  );
}
